import { LocalizedEventData } from '@carlito/bookito-client';
import { LineClamper } from '@carlito/dashboard-ui';
import { applyTimeOfDate } from '@carlito/date-utils';
import classNames from 'classnames';
import { isEventAllDay } from '../BookitoForm/helper';
import formatPrice, { findOccurrenceForDate } from '../utils';

type CardProps = { event: LocalizedEventData; openModal: () => void; onDate: Date };

const ExperienceCard = ({ event, openModal, onDate }: CardProps) => {
  const { title, basePrice, description, bannerImg } = event;

  const occurrence = findOccurrenceForDate(onDate, event) ?? event.occurrences[0];

  const startDateTime = applyTimeOfDate(onDate, new Date(occurrence.startDateTime));
  const endDateTime = new Date(startDateTime);
  endDateTime.setTime(endDateTime.getTime() + occurrence.durationMs);

  const startDateTimeStr = startDateTime.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });

  const endDateTimeStr = endDateTime.toLocaleString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });

  const isAllDay = isEventAllDay(onDate, event);

  const shouldShowPrice = Boolean(basePrice?.amount);
  const shouldShowTimes = !isAllDay;

  return (
    <div
      onClick={() => {
        openModal();
      }}
      role="button"
      style={{ backgroundImage: `url(${bannerImg?.url})` }}
      className="bookito-widget__card relative mr-5 min-h-full p-5 min-w-lg bg-no-repeat bg-cover bg-white border border-gray-200 rounded-3xl shadow dark:bg-gray-800 dark:border-gray-700 text-left overflow-hidden"
    >
      <div className="bookito-widget__card__gradient absolute w-full h-full top-0 left-0 bg-gradient-to-t from-neutral-900"></div>
      <div className="w-[80vw] md:w-80 lg:w-96 relative z-1 flex flex-col justify-between h-full">
        {title && (
          <div className="mb-10">
            <h5 className="bookito-widget_card__title rounded-md w-fit max-w-full truncate px-2 bg-white text-xl font-black tracking-tight text-gray-900 dark:text-black">
              {title}
            </h5>
          </div>
        )}

        <div>
          {description && (
            <div
              className={classNames(
                'bookito-widget__card__description pb-3 dark:text-white text-gray-50 text-xs',
                {
                  'text-justify': shouldShowPrice && shouldShowTimes,
                  'text-left': shouldShowPrice && !shouldShowTimes,
                  'text-right': !shouldShowPrice && shouldShowTimes,
                }
              )}
            >
              <LineClamper lines={3}>{description}</LineClamper>
            </div>
          )}

          <div className="flex items-end justify-between">
            <h5 className="bookito-widget__card__price text-2xl font-bold tracking-tight text-gray-50 dark:text-white">
              {shouldShowPrice ? formatPrice(basePrice?.amount ?? 0) : undefined}
            </h5>

            <h5
              className={classNames(
                'bookito-widget__card__time text-lg font-bold tracking-tight text-gray-50 dark:text-white',
                {
                  invisible: !shouldShowTimes,
                }
              )}
            >
              {startDateTimeStr} - {endDateTimeStr}
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceCard;
